import { useEffect, useState } from "react";
import { SystemCheckConfigurations } from "../components/layout/system-check-steps/system-check-steps";

export type SystemCheckConfiguration = {
  systemCompatibility?: {
    enable?: boolean;
    browserCheck?: {
      enable?: boolean;
    };
    deviceCheck?: {
      enable?: boolean;
    };
    operatingSystemCheck?: {
      enable?: boolean;
    };
  };
  screenCheck?: {
    enable?: boolean;
    resolutionCheck?: {
      enable?: boolean;
    };
    multipleMonitorCheck?: {
      enable?: boolean;
      screenSharing?: {
        enable?: boolean;
      };
    };
  };
  webcamCheck?: {
    enable?: boolean;
  };
  microphoneCheck?: {
    enable?: boolean;
  };
  networkCheck?: {
    enable?: boolean;
    urlCheck?: {
      enable?: boolean;
    };
    networkDownloadCheck?: {
      enable?: boolean;
    };
    networkUploadCheck?: {
      enable?: boolean;
    };
  };
};

export type Modality = {
  name: string;
  configurations: {
    systemCheckConfiguration: SystemCheckConfiguration;
  };
};

export type ExamConfiguration = {
  modality: Modality;
};

const DEFAULT_SYSTEM_CHECK_CONFIG: SystemCheckConfigurations = {
  browserCheck: {
    enable: true,
  },
  deviceCheck: {
    enable: true,
  },
  operatingSystemCheck: {
    enable: true,
  },
  screenResolutionCheck: {
    enable: true,
  },
  multipleMonitorCheck: {
    enable: true,
  },
  webcamCheck: {
    enable: true,
  },
  microphoneCheck: {
    enable: true,
  },
  urlCheck: {
    enable: true,
  },
  networkDownloadCheck: {
    enable: true,
  },
  networkUploadCheck: {
    enable: true,
  },
};

function useExamConfigMapping(
  examConfiguration?: ExamConfiguration,
  enableAllSystemCheckCfg?: boolean,
) {
  const [systemCheckConfig, setSystemCheckConfig] =
    useState<SystemCheckConfigurations>();

  const buildSystemCheckConfig = (
    examConfiguration: ExamConfiguration,
  ): SystemCheckConfigurations => {
    const configurations = examConfiguration.modality.configurations;
    const systemCheckConfig = configurations.systemCheckConfiguration;
    const systemCompatibilityEnabled =
      systemCheckConfig?.systemCompatibility?.enable ?? false;
    const networkEnabled = systemCheckConfig?.networkCheck?.enable ?? false;
    const screenCheckEnabled = systemCheckConfig?.screenCheck?.enable ?? false;
    const screenSharingEnabled =
      (screenCheckEnabled &&
        systemCheckConfig?.screenCheck?.multipleMonitorCheck?.enable &&
        systemCheckConfig?.screenCheck?.multipleMonitorCheck?.screenSharing
          ?.enable) ??
      false;

    const mappedObject: SystemCheckConfigurations = {
      browserCheck: {
        enable:
          systemCompatibilityEnabled &&
          (systemCheckConfig?.systemCompatibility?.browserCheck?.enable ??
            false),
      },
      deviceCheck: {
        enable:
          systemCompatibilityEnabled &&
          (systemCheckConfig?.systemCompatibility?.deviceCheck?.enable ??
            false),
      },
      operatingSystemCheck: {
        enable:
          systemCompatibilityEnabled &&
          (systemCheckConfig?.systemCompatibility?.operatingSystemCheck
            ?.enable ??
            false),
      },
      screenResolutionCheck: {
        enable:
          screenCheckEnabled &&
          (systemCheckConfig?.screenCheck?.resolutionCheck?.enable ?? false),
      },
      multipleMonitorCheck: {
        enable:
          screenCheckEnabled &&
          (systemCheckConfig?.screenCheck?.multipleMonitorCheck?.enable ??
            false),
      },
      screenSharingCheck: {
        enable: screenSharingEnabled,
      },
      webcamCheck: {
        enable: systemCheckConfig?.webcamCheck?.enable ?? false,
      },
      microphoneCheck: {
        enable: systemCheckConfig?.microphoneCheck?.enable ?? false,
      },
      urlCheck: {
        enable:
          networkEnabled &&
          (systemCheckConfig?.networkCheck?.urlCheck?.enable ?? true),
      },
      networkDownloadCheck: {
        enable:
          networkEnabled &&
          (systemCheckConfig?.networkCheck?.networkDownloadCheck?.enable ??
            false),
      },
      networkUploadCheck: {
        enable:
          networkEnabled &&
          (systemCheckConfig?.networkCheck?.networkUploadCheck?.enable ??
            false),
      },
    };

    return mappedObject;
  };

  useEffect(() => {
    if (!examConfiguration || enableAllSystemCheckCfg) {
      setSystemCheckConfig(DEFAULT_SYSTEM_CHECK_CONFIG);
      return;
    }

    setSystemCheckConfig(buildSystemCheckConfig(examConfiguration));
  }, [examConfiguration, enableAllSystemCheckCfg]);

  return { systemCheckConfig };
}

export default useExamConfigMapping;
